<template>
  <StickyContainer class="main-header-container" id="page-header" :top="0">
    <header
      class="main-header"
      :class="{ 'has-categories': categories?.length }"
    >
      <div
        class="btn-sidebar-container"
        v-if="categories?.length && viewport.isLessThan('lg')"
      >
        <button
          class="btn-sidebar m-cgg-icon--hamburger"
          @click="categoriesSidebar?.toggleDrawer()"
          :aria-label="t('aria.menu')"
        />
      </div>

      <NavbarLogo
        v-bind="{ logo }"
        :navigation-disabled="homeNavigationDisabled"
      />

      <div
        class="content-container"
        v-if="categories?.length && viewport.isGreaterOrEquals('lg')"
      >
        <nav class="categories-container">
          <div class="menu-category" v-for="(category, i) in categories">
            <NuxtLink
              external
              class="category-label"
              :href="getStoryblokUrl(category.link)"
              :id="`navbar-${i + 1}`"
            >
              {{ category.text }}
              <span
                v-if="category.links?.length"
                class="m-cgg-icon--chevron-down"
              />
            </NuxtLink>
            <Transition name="fade">
              <div
                class="category-links-container"
                v-if="category.links?.length"
              >
                <ul>
                  <li v-for="(menuLink, ii) in category.links">
                    <div class="menu-link-group" v-if="'links' in menuLink">
                      <div class="group-title">
                        {{ menuLink.title }}
                      </div>
                      <ul>
                        <li v-for="(subMenuLink, iii) in menuLink.links">
                          <NuxtLink
                            external
                            :id="`sub-navbar-${i + 1}-${ii + 1}-${iii + 1}`"
                            :href="getStoryblokUrl(subMenuLink.link)"
                          >
                            {{ subMenuLink.text }}
                          </NuxtLink>
                        </li>
                      </ul>
                    </div>
                    <NuxtLink
                      external
                      :id="`sub-navbar-${i + 1}-${ii + 1}`"
                      :href="getStoryblokUrl(menuLink.link)"
                      v-else
                    >
                      {{ menuLink.text }}
                    </NuxtLink>
                  </li>
                </ul>
              </div>
            </Transition>
          </div>
        </nav>
      </div>

      <div class="save-btn-container" v-if="sideBtn">
        <NavbarSaveButton
          :navbar-btn-id="sideBtn.navbarBtnId"
          :newsletter-btn-id="sideBtn.newsletterSubmitBtnId"
          ref="saveButton"
        />
      </div>
    </header>
  </StickyContainer>

  <Drawer
    :type="'horizontal-left'"
    :maxWidth="{
      md: '300px',
    }"
    :position="{ top: '60px', bottom: '0px' }"
    :defaultActive="false"
    :backdrop="true"
    ref="categoriesSidebar"
    v-if="viewport.isLessThan('lg')"
  >
    <nav class="categories-sidebar-wrapper">
      <div class="menu-category" v-for="(category, i) in categories">
        <CollapseContainer v-if="category.links?.length">
          <template #header>
            <div class="menu-category-link">
              {{ category.text }}
            </div>
          </template>
          <template #content>
            <ul class="category-links-container">
              <li v-if="category.link">
                <NuxtLink
                  external
                  class="menu-link"
                  :id="`navbar-${i + 1}`"
                  :href="getStoryblokUrl(category.link)"
                  @click="navigateToUrl()"
                >
                  {{ category.text }}
                </NuxtLink>
              </li>
              <li v-for="(menuLink, ii) in category.links">
                <div class="menu-link-group" v-if="'links' in menuLink">
                  <div class="group-title">
                    {{ menuLink.title }}
                  </div>
                  <ul>
                    <li v-for="(subMenuLink, iii) in menuLink.links">
                      <NuxtLink
                        external
                        class="menu-link"
                        :id="`sub-navbar-${i + 1}-${ii + 1}-${iii + 1}`"
                        :href="getStoryblokUrl(subMenuLink.link)"
                        @click="navigateToUrl()"
                      >
                        {{ subMenuLink.text }}
                      </NuxtLink>
                    </li>
                  </ul>
                </div>
                <NuxtLink
                  external
                  class="menu-link"
                  :id="`sub-navbar-${i + 1}-${ii + 1}`"
                  :href="getStoryblokUrl(menuLink.link)"
                  @click="navigateToUrl()"
                  v-else
                >
                  {{ menuLink.text }}
                </NuxtLink>
              </li>
            </ul>
          </template>
        </CollapseContainer>
        <NuxtLink
          external
          class="menu-category-link menu-link"
          :href="getStoryblokUrl(category.link)"
          @click="navigateToUrl()"
          v-else
        >
          {{ category.text }}
        </NuxtLink>
      </div>
    </nav>
  </Drawer>
</template>

<script lang="ts" setup>
import {
  MenuCategoryStoryblok,
  NavbarLogoImageStoryblok,
  NavbarLogoVideoStoryblok,
} from "~/types/bloks";

defineProps<{
  logo: NavbarLogoImageStoryblok[] | NavbarLogoVideoStoryblok[];
  homeNavigationDisabled?: boolean;
  categories?: MenuCategoryStoryblok[];
  sideBtn?: any;
}>();

const { t } = useI18n();
const viewport = useViewport();
const categoriesSidebar = ref();
const saveButton = ref();

const navigateToUrl = () => {
  categoriesSidebar.value.closeDrawer();
};
</script>

<style lang="scss" scoped>
.main-header-container {
  z-index: $level-navbar;
  box-shadow: $box-shadow-m;

  &.no-shadow {
    box-shadow: none;
  }

  .main-header {
    position: relative;
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 12px;
    background-color: $white;
    height: 60px;
    padding: 12px 24px;

    @include screen-xl-min {
      gap: 24px;
    }

    &.has-categories {
      @include screen-md-max {
        grid-template-columns: auto auto 1fr;
      }
    }

    .save-btn-container {
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }
  }
}

.categories-container {
  display: none;

  @include screen-lg-min {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 16px;
  }

  .menu-category {
    position: relative;
    padding-bottom: 21px;
    margin-bottom: -21px;

    &:hover a.category-label {
      color: $main-blue;
    }

    &:last-child .category-label {
      padding-left: 16px;
      border-left: 1px solid $blue-grey;
    }

    .category-label {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 5px;
      font-size: 12px;
      line-height: 18px;
      font-weight: 700;

      @include screen-xl-min {
        font-size: 14px;
      }
    }

    a.category-label {
      color: inherit;
      text-decoration: none;
      transition: all 0.3s ease-in-out;
    }

    .category-links-container {
      position: absolute;
      opacity: 0;
      transition: opacity 0.3s ease-in-out, transform 0s linear 0.3s;
      top: 100%;
      left: 50%;
      transform: scale(0);
      padding: 16px;
      background: $white;
      box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.1);
      border-bottom-left-radius: 4px;
      border-bottom-right-radius: 4px;

      ul {
        display: flex;
        flex-direction: column;
        gap: 16px;
        list-style: none;
        margin: 0;
        padding: 0;

        ul {
          margin-bottom: 16px;
        }

        .group-title {
          color: $blue-grey;
          font-size: 14px;
          font-weight: 700;
          padding-bottom: 8px;
          margin-bottom: 8px;
          border-bottom: 1px solid $disabled-grey;
        }

        a {
          font-size: 14px;
          line-height: 18px;
          font-weight: 700;
          color: inherit;
          text-decoration: none;
          transition: all 0.3s ease-in-out;
          white-space: nowrap;

          &:hover {
            color: $main-blue;
          }
        }
      }
    }

    &:hover {
      .category-links-container {
        opacity: 1;
        transform: translateX(-50%);
        transition: opacity 0.3s ease-in-out, transform 0s linear;
      }
    }
  }
}

.btn-sidebar-container {
  .btn-sidebar {
    display: block;
    background: none;
    border: none;
    padding: 0;
    width: 25px;
    height: 25px;
    font-size: 20px;
    line-height: 25px;
  }
}

.categories-sidebar-wrapper {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: $white;
  padding: 24px;

  :deep(.collapse-container) {
    .collapse-header {
      padding: 15px 0;
    }

    .content-wrapper {
      padding: 0;
    }

    &.active {
      .collapse-header {
        color: $main-blue;
      }
    }
  }

  .category-links-container {
    display: flex;
    flex-direction: column;
    list-style: none;
    margin: 0;
    padding: 0 0 16px;
    font-size: 14px;
    line-height: 18px;

    .menu-link-group {
      margin-top: 24px;
    }

    .group-title {
      color: $blue-grey;
      font-size: 14px;
      font-weight: 700;
      padding-bottom: 12px;
      margin-bottom: 12px;
      border-bottom: 1px solid $disabled-grey;
    }

    ul {
      display: flex;
      flex-direction: column;
      list-style: none;
      padding: 0;
      margin: 0;
    }
  }

  .menu-link {
    display: block;
    color: inherit;
    padding: 15px 0;

    &:hover {
      text-decoration: none;
    }
  }

  .menu-category-link {
    font-weight: 700;
    font-size: 14px;
    line-height: 18px;
  }
}

.modal-title {
  font-size: 16px;
}
</style>

<i18n lang="json">
{
  "pt": {
    "aria": {
      "menu": "Menu",
      "contact": "Contactar"
    }
  }
}
</i18n>
